import React, { useEffect, useState } from 'react';
import VideoPicker from '../video/VideoPicker';
import { TELL_ANTHONY } from '../../../../services/trackJobsModel';
import { describeSource } from '../../../../services/songReuseModel';
import './SongSetup.css';

/**
 * Setting up a song just added (Lesson Dashboard pass 4 S3, 2026-10-02;
 * Anthony's flow: "A youtube search for lyric videos is automatically started
 * and pops up. The teacher can select one and is prompted right away if they
 * would like to create a track. they can select yes or later").
 *
 * Inline at the top of the lyrics, never a modal, so the notes stay usable.
 * Step 1 is the existing picker, searching on open. Step 2 asks Yes / Later:
 * Yes queues the same job as "Make a track" from the saved video; Later
 * leaves Make a track one tap away on the Tracks row. Only for a song added
 * this lesson that has no lyric video yet (the page decides).
 *
 * Step 0 (2026-11-01; Anthony: "check if there is already a created track /
 * timed lyrics / etc and prompt the teacher to see if they would like to just
 * use those"): when another student already has this song set up, the card
 * offers that FIRST (`reuse`, hooks/useSongReuse.js). Use copies it and the
 * card goes on only to what is still missing: no lyric video, the search; a
 * lyric video and no Track, the Yes / Later ask; both, it closes. Start fresh
 * goes to the search as before. While the lookup runs the card says so in one
 * line, and a failed lookup reads as "nothing to offer", never as a block.
 */
const SHOWN_SOURCES = 3;

const firstNameOrOther = (s) => (s.firstName ? `${s.firstName}\u2019s` : 'Another student\u2019s');

const SongSetup = ({ song, video, videoTrack = null, reuse = null, onDone }) => {
	const [step, setStep] = useState(reuse ? 'reuse' : 'pick');
	// After a copy that brought a Track, a lyric video picked next does not
	// need the "make a track" question.
	const [hasTrack, setHasTrack] = useState(false);
	const [copying, setCopying] = useState(null);
	const [copyError, setCopyError] = useState('');
	// make() reports a failure on videoTrack.error rather than throwing, so the
	// card closes only once the queue shows the job, and stays to say so if
	// it never does.
	const [asked, setAsked] = useState(false);
	const state = videoTrack && asked ? videoTrack.stateFor('lyrics') : null;
	useEffect(() => {
		if (state === 'working' || state === 'done') onDone();
	}, [state, onDone]);

	// Nothing to offer (or the lookup failed) reads straight through to the
	// search, derived rather than set so there is no frame of "Checking".
	const reuseStatus = reuse ? reuse.status : 'none';
	const view = step === 'reuse' && reuseStatus !== 'ready' && reuseStatus !== 'checking' ? 'pick' : step;

	if (view === 'reuse') {
		if (reuseStatus === 'checking') {
			return (
				<div className="ld-setup" role="status">
					<p className="ld-setup__hint ld-setup__hint--first">
						<i className="fa-sharp fa-solid fa-spinner-third fa-spin" aria-hidden="true" /> Checking whether another student has this song set up…
					</p>
				</div>
			);
		}
		const sources = reuse.sources.slice(0, SHOWN_SOURCES);
		const use = async (source) => {
			if (copying) return;
			setCopying(source.repertoireId);
			setCopyError('');
			try {
				const res = await reuse.apply(source);
				const broughtVideo = (res.brought || []).includes('lyric video');
				const broughtTrack = res.tracksAdded > 0 && source.hasTrack;
				setHasTrack(broughtTrack);
				if (!broughtVideo && !(song && song.videos && song.videos.lyrics)) setStep('pick');
				else if (!broughtTrack && videoTrack) setStep('ask');
				else onDone();
			} catch (e) {
				console.error('Copying another student\u2019s setup:', e);
				setCopyError(e && e.message ? e.message : String(e));
			} finally {
				setCopying(null);
			}
		};
		return (
			<div className="ld-setup ld-setup--reuse" role="group" aria-label="Use another student's setup of this song">
				<p className="ld-setup__head">
					<i className="fa-sharp fa-solid fa-clone" aria-hidden="true" />
					Already set up for {sources.length === 1 ? 'another student' : 'other students'}
				</p>
				<p className="ld-setup__body">
					Use what is on file instead of starting over. Their tracks are shared, not copied, and nothing changes for them.
				</p>
				<ul className="ld-setup__sources">
					{sources.map((s, i) => (
						<li key={s.repertoireId} className="ld-setup__source">
							<button
								type="button"
								className={`ld-setup__btn ld-setup__source-btn${i === 0 ? ' ld-setup__btn--primary' : ''}`}
								disabled={Boolean(copying)}
								onClick={() => use(s)}>
								<span className="ld-setup__source-name">
									{copying === s.repertoireId ? 'Copying…' : `Use ${firstNameOrOther(s)}`}
								</span>
								<span className="ld-setup__source-what">{describeSource(s)}</span>
							</button>
						</li>
					))}
				</ul>
				<div className="ld-setup__acts">
					<button type="button" className="ld-setup__btn" disabled={Boolean(copying)} onClick={() => !copying && setStep('pick')}>
						Start fresh
					</button>
				</div>
				<p className="ld-setup__hint">Their notes, their key and their recital choices stay theirs.</p>
				{copyError && (
					<div role="alert">
						<p className="ld-setup__failhead">The setup could not be copied. {TELL_ANTHONY}</p>
						<p className="ld-setup__hint">{copyError}</p>
					</div>
				)}
			</div>
		);
	}

	if (view === 'pick') {
		return (
			<div className="ld-setup">
				{/* Keyed by song: the picker holds its query and results in its own
				    state, so without a key a picker left open while the song changes
				    keeps the LAST song's results, and one of them can be saved onto
				    this song. Defensive: Addison's Placido carried another student's
				    "On My Own" (2026-10-22) by a path not yet proven. A new song gets a
				    fresh search. */}
				<VideoPicker
					key={song?.id || 'none'}
					song={song}
					kind="lyrics"
					autoSearch
					heading={<>Pick a <strong>lyric video</strong> for {song?.metadata?.title || song?.name}</>}
					cancelLabel="Not now"
					draft={video.draft}
					setDraft={video.setDraft}
					error={video.error}
					onPickUrl={async (url) => {
						await video.save(url, 'lyrics');
						if (videoTrack && !hasTrack) setStep('ask');
						else onDone();
					}}
					onSaveDraft={async () => {
						await video.save(undefined, 'lyrics');
						if (videoTrack && !hasTrack) setStep('ask');
						else onDone();
					}}
					onCancel={onDone}
				/>
			</div>
		);
	}

	return (
		<div className="ld-setup ld-setup--ask" role="group" aria-label="Make a track from the lyric video">
			<p className="ld-setup__head">
				<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
				Lyric video saved.
			</p>
			<p className="ld-setup__body">Make a track from it? A studio Mac removes the vocals while you keep teaching.</p>
			<div className="ld-setup__acts">
				<button
					type="button"
					className="ld-setup__btn ld-setup__btn--primary"
					disabled={asked && !videoTrack.error}
					onClick={() => {
						if (asked && !videoTrack.error) return;
						setAsked(true);
						videoTrack.make('lyrics');
					}}>
					<i className="fa-sharp fa-solid fa-wave-square" aria-hidden="true" />
					{asked && !videoTrack.error ? 'Starting…' : 'Yes, make the track'}
				</button>
				<button type="button" className="ld-setup__btn" onClick={onDone}>
					Later
				</button>
			</div>
			<p className="ld-setup__hint">Later keeps it one tap away: Tracks, Make a track.</p>
			{asked && videoTrack.error && (
				<div role="alert">
					<p className="ld-setup__failhead">The track could not be started. {TELL_ANTHONY}</p>
					<p className="ld-setup__hint">{videoTrack.error}</p>
				</div>
			)}
		</div>
	);
};

export default SongSetup;
